import React, { FC } from 'react';
import {
  BooleanInput,
  DateInput,
  Filter,
  FilterProps,
  TextInput,
} from 'react-admin';
import { LazyReferenceInput } from 'components/LazyAutoCompleteInput';
import { UserRoleMultiReferenceInput } from 'components/UserRoleMultiReferenceInput';

type Props = {
  showSupplier?: boolean;
  /** Purchase Order creator (`Ordered By`) and creation `Timestamp` filters. */
  showCreatedBy?: boolean;
} & Omit<FilterProps, 'children'>;

const ORDERED_BY_ROLES = ['RegionalAdmin', 'SuperAdmin', 'MaterialDispatcher'];

export const MaterialDeliveryFilter: FC<Props> = ({
  showSupplier = true,
  showCreatedBy = true,
  ...props
}) => {
  const ScreenSize = window.screen.width;
  const dateInputWidth = ScreenSize <= 849 && ScreenSize >= 200 ? 280 : '100%';
  return (
    <Filter {...props}>
      {showSupplier && (
        <LazyReferenceInput
          label="Supplier"
          source="supplierId"
          reference="supplier"
          resettable
          data-cy="MaterialDeliveryFilterSupplier"
          allowEmpty={false}
          filterToQuery={value => (value ? { name: value } : {})}
          sort={{ field: 'name', order: 'ASC' }}
          optionText="name"
          optionValue="id"
        />
      )}
      <TextInput
        label="Purchase Order #"
        source="orderNumber||$cont"
        resettable
        data-cy="MaterialDeliveryFilterPurchaseOrder"
        alwaysOn
      />
      {showCreatedBy && (
        <UserRoleMultiReferenceInput
          label="Ordered By"
          source="createdByUserId"
          roles={ORDERED_BY_ROLES}
          referenceForGetOne="user"
          resettable
          data-cy="MaterialDeliveryFilterCreatedByUser"
          allowEmpty={false}
        />
      )}
      {showCreatedBy && (
        <DateInput
          style={{ width: dateInputWidth }}
          label="Timestamp On/After"
          source="orderDate||$gte"
          data-cy="MaterialDeliveryFilterTimestampOnAfter"
        />
      )}
      {showCreatedBy && (
        <DateInput
          style={{ width: dateInputWidth }}
          label="Timestamp On/Before"
          source="orderDate||$lte"
          data-cy="MaterialDeliveryFilterTimestampOnBefore"
        />
      )}
      <LazyReferenceInput
        label="Builders"
        source="builderId"
        reference="builder"
        resettable
        data-cy="MaterialDeliveryFilterBuilder"
        allowEmpty={false}
        filterToQuery={name => (name ? { name } : {})}
        sort={{ field: 'name', order: 'ASC' }}
        optionText="name"
        optionValue="id"
      />
      <LazyReferenceInput
        label="Subdivision"
        source="subdivisionId"
        reference="subdivision"
        data-cy="MaterialDeliveryFilterSubdivision"
        resettable
        allowEmpty={false}
        filterToQuery={name => (name ? { name } : {})}
        sort={{ field: 'name', order: 'ASC' }}
        optionText="name"
        optionValue="id"
      />
      <TextInput
        label="Lot"
        source="jobLot||$eq"
        resettable
        data-cy="MaterialDeliveryFilterLot"
        alwaysOn
      />
      <TextInput
        label="Block"
        source="jobBlock||$eq"
        resettable
        data-cy="MaterialDeliveryFilterBlock"
        alwaysOn
      />
      <TextInput
        label="Address"
        source="fullAddress||$contL"
        resettable
        data-cy="MaterialDeliveryFilterAddress"
        alwaysOn
      />
      <DateInput
        style={{ width: dateInputWidth }}
        label="Expected Date"
        source="dateExpected||$eq"
        data-cy="MaterialDeliveryFilterDateExpected"
        alwaysOn
      />
      <DateInput
        style={{ width: dateInputWidth }}
        label="Promised Date"
        source="datePromised||$eq"
        placeholder="dd/mm/yyyy"
        data-cy="MaterialDeliveryFilterDatePromised"
        alwaysOn
      />
      <DateInput
        style={{ width: dateInputWidth }}
        label="Delivery Date"
        source="dateDelivery||$eq"
        data-cy="MaterialDeliveryFilterDateDelivery"
        alwaysOn
      />
      <BooleanInput
        source="noPromissedDate||$eq"
        label="No Promised Date"
        data-cy="MaterialDeliveryFilterPromisedDate"
        defaultValue={false}
        alwaysOn
      />
      <BooleanInput
        source="noDeliveryDate||$eq"
        label="No Delivery Date"
        data-cy="MaterialDeliveryFilterDeliveryDate"
        defaultValue={false}
        alwaysOn
      />
      <BooleanInput
        source="late||$eq"
        label="Late"
        data-cy="MaterialDeliveryFilterPromisedDate"
        defaultValue={false}
        alwaysOn
      />
    </Filter>
  );
};
